import { Injectable, signal, computed, NgZone, inject } from '@angular/core';

/**
 * Detects device / browser capability so the UI can degrade gracefully on
 * low-end hardware, constrained data plans, and accessibility-tuned
 * setups — the device-side companion to {@link NetworkStatusService}.
 *
 * Three independent signals are derived from browser hints:
 *
 *   - `saveData()`     — the user has "Data Saver" / "Reduce data usage"
 *                        on (`navigator.connection.saveData`).
 *   - `reducedMotion()`— the OS "reduce motion" accessibility setting is
 *                        on (`prefers-reduced-motion: reduce`).
 *   - `lowEnd()`       — the device looks under-powered: few logical CPU
 *                        cores (`hardwareConcurrency`), little RAM
 *                        (`deviceMemory`), or a slow effective connection
 *                        (`effectiveType` of `2g`/`slow-2g`).
 *
 * `constrained()` is the roll-up the rest of the app reads: true when ANY
 * of the above asks us to do less work (lighter animations, no autoplay,
 * no heavy decorative effects).
 *
 * The matching `<html>` classes (`save-data`, `reduced-motion`,
 * `low-end-device`) are applied by {@link applyCapabilityClasses} so plain
 * CSS — including the global stylesheet and `index.html`'s pre-bootstrap
 * fallback — can react without pulling in Angular. Everything is wrapped
 * defensively because every one of these APIs is optional and several
 * throw in locked-down / older browsers.
 */
@Injectable({ providedIn: 'root' })
export class CapabilityService {
  private zone = inject(NgZone);

  private readonly _saveData = signal<boolean>(this.readSaveData());
  private readonly _reducedMotion = signal<boolean>(this.readReducedMotion());
  private readonly _lowEnd = signal<boolean>(this.readLowEnd());

  /** User opted into reduced data usage. */
  readonly saveData = computed(() => this._saveData());
  /** OS-level "reduce motion" accessibility preference is on. */
  readonly reducedMotion = computed(() => this._reducedMotion());
  /** Device appears under-powered (few cores / little RAM / slow link). */
  readonly lowEnd = computed(() => this._lowEnd());

  /**
   * Roll-up flag: true when the app should do less work — skip autoplay,
   * drop decorative animations/blur, prefer lighter assets.
   */
  readonly constrained = computed(
    () => this._saveData() || this._reducedMotion() || this._lowEnd(),
  );

  constructor() {
    if (typeof window === 'undefined') return;

    // `prefers-reduced-motion` can change at runtime (user toggles the OS
    // setting); keep the signal live. `addEventListener` on MediaQueryList
    // is the modern API; the older `addListener` is a defensive fallback.
    const mq = this.motionQuery();
    if (mq) {
      const onChange = () => this.zone.run(() => this._reducedMotion.set(mq.matches));
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', onChange);
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(onChange);
      }
    }

    // Data-saver / effective connection type can also flip mid-session
    // (e.g. user enables Data Saver, or the radio drops to 2G).
    const conn = this.connection();
    if (conn && typeof conn.addEventListener === 'function') {
      conn.addEventListener('change', () =>
        this.zone.run(() => {
          this._saveData.set(this.readSaveData());
          this._lowEnd.set(this.readLowEnd());
        }),
      );
    }
  }

  private motionQuery(): MediaQueryList | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch {
      return null;
    }
  }

  // The Network Information API is non-standard and absent in Safari/Firefox;
  // typed loosely and read defensively.
  private connection(): (EventTarget & { saveData?: boolean; effectiveType?: string }) | null {
    if (typeof navigator === 'undefined') return null;
    const nav = navigator as Navigator & {
      connection?: EventTarget & { saveData?: boolean; effectiveType?: string };
    };
    return nav.connection ?? null;
  }

  private readSaveData(): boolean {
    return this.connection()?.saveData === true;
  }

  private readReducedMotion(): boolean {
    return this.motionQuery()?.matches ?? false;
  }

  private readLowEnd(): boolean {
    if (typeof navigator === 'undefined') return false;
    const nav = navigator as Navigator & { deviceMemory?: number };

    const cores = typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : undefined;
    if (cores !== undefined && cores > 0 && cores <= 2) return true;

    const memory = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : undefined;
    if (memory !== undefined && memory > 0 && memory <= 1) return true;

    const effectiveType = this.connection()?.effectiveType;
    if (effectiveType === '2g' || effectiveType === 'slow-2g') return true;

    return false;
  }
}

/**
 * Reflects the current capability flags onto the document element as CSS
 * classes (`save-data`, `reduced-motion`, `low-end-device`) so the global
 * stylesheet can degrade visuals without each component opting in. Safe to
 * call before Angular bootstrap and on SSR (no-ops without a `document`).
 */
export function applyCapabilityClasses(cap: CapabilityService): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('save-data', cap.saveData());
  root.classList.toggle('reduced-motion', cap.reducedMotion());
  root.classList.toggle('low-end-device', cap.lowEnd());
}
